"use client";

import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="140" className="flex gap-6">
        <div className="h-4 w-20 bg-muted/50 animate-pulse rounded-sm" />
        <div className="h-4 w-20 bg-muted/50 animate-pulse rounded-sm" />
      </div>;
  }
  const items = menuItems.length > 0 ? menuItems : [{
    id: "1",
    label: "Home",
    href: "/HomePage"
  }, {
    id: "2",
    label: "Services",
    href: "/ServicesPage"
  }, {
    id: "3",
    label: "Industries",
    href: "/IndustriesPage"
  }, {
    id: "4",
    label: "About",
    href: "/AboutPage"
  }, {
    id: "5",
    label: "Contact",
    href: "/ContactPage"
  }];
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-8">
        {items.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="text-sm font-sans font-medium tracking-tight text-foreground hover:text-primary transition-colors">
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onClose
}: {
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="p-4 text-sm text-muted-foreground">Loading menu...</div>;
  }
  const items = menuItems.length > 0 ? menuItems : [{
    id: "1",
    label: "Home",
    href: "/HomePage"
  }, {
    id: "2",
    label: "Services",
    href: "/ServicesPage"
  }, {
    id: "3",
    label: "Industries",
    href: "/IndustriesPage"
  }, {
    id: "4",
    label: "About",
    href: "/AboutPage"
  }, {
    id: "5",
    label: "Contact",
    href: "/ContactPage"
  }];
  return <nav className="flex flex-col gap-6 p-6">
      {items.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} className="text-lg font-sans font-medium text-foreground hover:text-primary transition-colors" onClick={onClose}>
          {item.label}
        </Link>)}
    </nav>;
}
export default function MainNavigation() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest('a[href]') as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute('href');
        if (!href) return;
        let hash = '';
        if (href.startsWith('#')) {
          hash = href;
        } else if (href.startsWith('/') && href.includes('#')) {
          const [path, hashPart] = href.split('#');
          if (path === '/' || path === window.location.pathname) {
            hash = '#' + hashPart;
          }
        }
        if (hash && hash !== '#') {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener('click', handleAnchorClick);
    return () => document.removeEventListener('click', handleAnchorClick);
  }, []);
  return <header id="header" className={`sticky top-0 z-50 w-full transition-all duration-300 border-b border-transparent ${isScrolled ? "bg-background/80 backdrop-blur-md border-border/40 py-4 shadow-sm" : "bg-background py-6"}`}>
      <div className="container mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between">
          {}
          <div className="flex-1 flex items-center justify-start">
            {}
            <div className="hidden md:block">
              <MenuProvider menu_id="16" className="w-full">
                <DesktopMenuConsumer />
              </MenuProvider>
            </div>

            {}
            <Button variant="ghost" size="icon" className="md:hidden -ml-2 text-foreground hover:text-primary hover:bg-transparent" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Toggle menu">
              {isMobileMenuOpen ? <X className="size-6" /> : <Menu className="size-6" />}
            </Button>
          </div>

          {}
          <div className="flex-0 flex justify-center">
            <Link to="/HomePage" className="block relative z-10">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1604077/img/mvp_pilot_logo.png" alt="Quantificial" className="h-7 w-auto object-contain" />
            </Link>
          </div>

          {}
          <div className="flex-1 flex items-center justify-end gap-4 md:gap-6">
            <Button asChild className="hidden md:inline-flex bg-primary hover:bg-primary/90 text-primary-foreground rounded-sm px-6 font-sans tracking-tight text-sm font-bold h-11 transition-all hover:-translate-y-0.5">
              <Link to="/ContactPage">Start a conversation</Link>
            </Button>
          </div>
        </div>
      </div>

      {}
      {isMobileMenuOpen && <div className="md:hidden absolute top-full left-0 w-full bg-background border-b border-border shadow-lg animate-in slide-in-from-top-5 fade-in duration-200">
          <MenuProvider menu_id="16" className="w-full">
            <MobileMenuConsumer onClose={() => setIsMobileMenuOpen(false)} />
          </MenuProvider>
          <div className="p-6 pt-0">
            <Button asChild className="w-full bg-primary hover:bg-primary/90 text-primary-foreground rounded-sm font-sans tracking-tight font-bold">
              <Link to="/ContactPage" onClick={() => setIsMobileMenuOpen(false)}>
                Start a conversation
              </Link>
            </Button>
          </div>
        </div>}
    </header>;
}